import { Container } from "@material-ui/core";
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Switch from '@material-ui/core/Switch';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import PageTitle from '../../components/ui/PageTitleUI';
import { useAuth } from '../../hooks/auth';
import MvmApi from '../../services/MvmApi';
import permissions from '../../utils/Permissions';
import CheckIn from './CheckIn';
import CheckOut from './CheckOut';
import EntryPlant from './EntryPlant';
import ResubmissionRequisition from './ResubmissionRequisition';
import { useStyles } from './Style';
import VehiclesOnConcierge from './VehiclesOnConcierge';
import VehiclesWatingNF from "./VehiclesWaitingNF";
import { FIASA_PLANT_ID } from '../../utils/constants'

interface PageParams {
  tabIndex?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const AuthorizeEntryPlant: React.FC = () => {
  const RELOAD_INTERVAL_MS = 30 * 1000;
  const pageParams = useParams<PageParams>()
  const history = useHistory();
  const classes = useStyles();
  const { t: _t } = useTranslation("General");
  const { t: t } = useTranslation("AuthorizeEntryPlant");
  const [entryPlantCount, setEntryPlantCount] = React.useState("0");
  const [checkinCount, setCheckinCount] = React.useState("0");
  const [checkoutCount, setCheckoutCount] = React.useState("0");
  const { user, hasPermission } = useAuth();
  const [value, setValue] = React.useState(0);
  const [radioValue, setradioValue] = React.useState('1');
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const [bloquedXD, setBloquedXD] = React.useState(false);
  const [canViewbloquedXD, setCanViewBloquedXD] = React.useState(false);
  const [reloadOnCLick, setReloadOnCLick] = React.useState(0);
  const lastReloadAtRef = React.useRef(0);
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setradioValue((event.target as HTMLInputElement).value);
  };

  const onReloadPage = (value: string) => {
    if (radioValue === value) {
      const now = Date.now();
      const timeSinceLastReload = now - lastReloadAtRef.current;

      if (timeSinceLastReload < RELOAD_INTERVAL_MS) {
        return;
      }

      lastReloadAtRef.current = now;
      setReloadOnCLick((prev) => prev + 1);
    } else {
      setReloadOnCLick(0);
    }
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (!hasPermission(permissions.SHED.Manage))
      history.replace('NoPermission');
  }, [history, user])

  useEffect(() => {
    setEntryPlantCount("0")
    setCheckinCount("0")
    setCheckoutCount("0")

    if (hasPermission(permissions.XD.CanBloqued)) {
      MvmApi.getIsBloquedXD(user.company.plantID).then(result => setBloquedXD(result));
      setCanViewBloquedXD(hasPermission(permissions.XD.CanBloqued) && +user.company.plantID === FIASA_PLANT_ID);
      if (pageParams.tabIndex) {
        setradioValue(pageParams.tabIndex);
      }
    }
  }, []);

  const getCounts = () => {
    setOpenBackdrop(false);

    MvmApi.getEntryAuthorizationCounts(+user.company.plantID, user.userName, +user.company.companyID)
      .then(result => {
        if (result) {
          setEntryPlantCount(result.entryPlantCount.toString())
          setCheckinCount(result.checkinCount.toString())
          setCheckoutCount(result.checkoutCount.toString())
        }
        else {
          setEntryPlantCount("0")
          setCheckinCount("0")
          setCheckoutCount("0")
        }
        setOpenBackdrop(false);
      }).catch(() => {
        setEntryPlantCount("0")
        setCheckinCount("0")
        setCheckoutCount("0")
        setOpenBackdrop(false);
      })
  }

  const handleBloquedXD = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBloquedXD(!bloquedXD);
    MvmApi.saveBloquedXD(!bloquedXD, user.company.plantID);
  };

  return (
    <Container maxWidth={false} component="main">
      <Backdrop className={classes.backdrop} open={openBackdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <PageTitle title={t("manageShed")} />

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label={t("unloadLoadLabel")} {...a11yProps(0)} />
            <Tab label={t("entryVehiclesLabel")} {...a11yProps(1)} />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>

          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={radioValue}
                onChange={handleRadioChange}
              >
                <FormControlLabel value="1" control={<Radio onClick={() => onReloadPage("1")} />} label={`${t("entryPlant")} (${entryPlantCount})`} />
                <FormControlLabel value="2" control={<Radio onClick={() => onReloadPage("2")} />} label={`${t("checkInLabel")} (${checkinCount})`} />
                <FormControlLabel value="3" control={<Radio onClick={() => onReloadPage("3")} />} label={`${t("checkOutLabel")} (${checkoutCount})`} />
                {+user.company.plantID === FIASA_PLANT_ID
                  && <FormControlLabel value="4" control={<Radio onClick={() => onReloadPage("4")} />} label={`${t("resubmissionRequisitionLabel")}`} />}
                {+user.company.plantID === FIASA_PLANT_ID
                  && <FormControlLabel value="5" control={<Radio onClick={() => onReloadPage("5")} />} label={`${t("vehicleWatingNF")}`} />}
                {canViewbloquedXD && <FormControlLabel value="4" control={<Switch checked={bloquedXD} color="primary" onChange={handleBloquedXD} />} label={"Bloquear XD"} />}
              </RadioGroup>
            </FormControl>
          </div>
          <div className={classes.rootContent} style={{ margin: 0 }}>
            <div className={classes.paper}>
              {radioValue == '1' ?
                <EntryPlant getCounts={getCounts} reloadOnCLick={reloadOnCLick} /> :
                radioValue == '2' ?
                  <CheckIn getCounts={getCounts} reloadOnCLick={reloadOnCLick} /> :
                  radioValue == '3' ?
                    <CheckOut getCounts={getCounts} reloadOnCLick={reloadOnCLick} /> :
                    radioValue == '4' ?
                      <ResubmissionRequisition getCounts={getCounts} reloadOnCLick={reloadOnCLick} /> :
                      <VehiclesWatingNF getCounts={getCounts} reloadOnCLick={reloadOnCLick} />}
            </div>
          </div>
        </TabPanel>
        <TabPanel value={value} index={1}>
          <div className={classes.rootContent} style={{ margin: 0 }}>
            <div className={classes.paper}>
              <VehiclesOnConcierge />
            </div>
          </div>
        </TabPanel>
      </Box>
    </Container>
  )
}
export default AuthorizeEntryPlant;
